<script>

  import { External, M } from '../external';
  import AddonsList from './AddonsList.svelte';
  import { fade } from 'svelte/transition';
  import { createEventDispatcher, onMount, tick } from 'svelte';
  import BillingInfo from './BillingInfo.svelte';
  import { makeWebsiteRequest } from '../Common/tools';

  export let plan, currentPlan, vatRates, paymentProcessor, currency = '';

  const dispatch = createEventDispatcher();

  let billingInstance = null;
  let yearlyPerMonth = 0;
  let monthlyPrice = 0;
  let saving = 0;
  let selectedPeriod = '';
  let planFullPriceNoTax = 0;
  let creditNoTax = 0;
  let taxPercent = 0;
  let totalNoTax = 0;
  let tax = 0;
  let totalWithTax = 0;
  let hasDiscount = false;
  let discountFieldVisible = false;
  let discountCode = '';
  let discountPercent = 0;
  let discountValue = 0;
  let applyDiscountOnNextPayment = false;
  let nextBillingAmount = 0;
  let nextBillingDate = '';
  let isLifetime = false;
  let promoCodeFieldValue = '';
  let activeVatPercent = 0;
  let activeVatId = '';
  let loading = false;
  let subscribing = false;

  let prices = {
    yearlyPlanNextBillingDate: 0,
    yearlyPriceForUpgrade: 0,
    yearlyPriceForNewAccountType: 0,
    calculatedYarlyPrice: 0,
    monthlyPlanNextBillingDate: '',
    monthlyPriceForUpgrade: 0,
    monthlyPriceForNewAccountType: 0,
    calculatedMonthlyPrice: 0
  };


  let cancel = () => {
    dispatch('cancel');
  };

  let init = async () => {
    loading = true;
    let result = await makeWebsiteRequest({
      action: 'calculatePrice',
      data: {
        plan: plan.id
      }
    });

    if (!result.ok) {
      loading = false;
      cancel();
      return;
    }

    prices = {
      ...prices,
      ...result.data
    };
    selectedPeriod = window.forcedSubscriptionPeriod || currentPlan.billingPeriod || 'YEARLY';
    activeVatPercent = currentPlan.activeVatPercent;
    activeVatId = currentPlan.billingInfo?.vatId || '';
    refreshPlanTotalPrices();
    loading = false;
  };

  let onPromoCodeKeyUp = (event) => {
    if (!discountFieldVisible || subscribing) {
      return;
    }
    if (event.keyCode === 13) {
      applyPromoCode();
    } else if (event.keyCode === 27) {
      removePromoCode();
    }
  };

  let applyPromoCode = async () => {
    if (!discountFieldVisible || subscribing) {
      return;
    }
    let result = await makeWebsiteRequest({
      action: 'checkPromoCode',
      data: {
        code: promoCodeFieldValue,
        plan: plan.id,
        billingPeriod: selectedPeriod
      }
    });

    if (!result.ok) {
      return;
    }

    if (result.data.isValid) {
      discountCode = result.data.code;
      discountPercent = result.data.discount;
      applyDiscountOnNextPayment = result.data.applyDiscountOnNextPayment;
      refreshPlanTotalPrices();
    } else {
      External.Tools.showError(M.INVALID_PROMO_CODE);
    }
  };

  let removePromoCode = () => {
    if (subscribing) {
      return;
    }
    discountCode = '';
    discountPercent = 0;
    applyDiscountOnNextPayment = false;
    discountValue = 0;
    hasDiscount = false;
    discountFieldVisible = false;
    refreshPlanTotalPrices();
  };

  let onTaxChange = (event) => {
    activeVatPercent = event.detail.activeVatPercent;
    activeVatId = event.detail.vatId;
    refreshPlanTotalPrices();
  };

  let refreshPlanTotalPrices = () => {
    let oldAmount = totalWithTax;
    let oldFutureAmount = nextBillingAmount;

    yearlyPerMonth = prices.yearlyPriceForNewAccountType / 12;
    monthlyPrice = prices.monthlyPriceForNewAccountType;
    saving = Math.round((prices.monthlyPriceForNewAccountType - yearlyPerMonth) / prices.monthlyPriceForNewAccountType * 100);

    if (External.platformId === 1 && currency === 'BGN') {
      let vatMultiplier = (100 + activeVatPercent) / 100;
      yearlyPerMonth = yearlyPerMonth * vatMultiplier;
      monthlyPrice = monthlyPrice * vatMultiplier;
    }

    planFullPriceNoTax = (selectedPeriod === 'YEARLY' ? prices.yearlyPriceForUpgrade : prices.monthlyPriceForUpgrade);

    totalNoTax = (selectedPeriod === 'YEARLY' ? prices.calculatedYarlyPrice : prices.calculatedMonthlyPrice);
    discountValue = 0;

    nextBillingAmount = selectedPeriod === 'YEARLY' ? prices.yearlyPriceForNewAccountType : prices.monthlyPriceForNewAccountType;

    hasDiscount = totalNoTax > 0 && discountPercent > 0;
    if (hasDiscount) {
      discountValue = totalNoTax * discountPercent / 100;
      if (applyDiscountOnNextPayment) {
        nextBillingAmount -= nextBillingAmount * discountPercent / 100;
      }
    }

    if (totalNoTax < 0) { // use old tax if refund!
      taxPercent = currentPlan.taxPercent;
    } else if (totalNoTax === 0) {
      taxPercent = 0;
    } else if (activeVatId && activeVatId.toUpperCase().indexOf(External.platformCountry) !== 0 && External.platformCountry !== 'NO') {
      taxPercent = 0;
    } else {
      taxPercent = activeVatPercent;
    }

    creditNoTax = totalNoTax - planFullPriceNoTax;
    totalNoTax = totalNoTax - discountValue;

    if (prices.pendingFees > 0.01) {
      totalNoTax += prices.pendingFees;
    }

    totalWithTax = totalNoTax * (1 + taxPercent / 100);
    tax = totalWithTax - totalNoTax;

    if (taxPercent > 0) {
      nextBillingAmount = nextBillingAmount * (100 + taxPercent) / 100;
    }

    isLifetime = nextBillingAmount < 0.01;
    nextBillingDate = selectedPeriod === 'YEARLY' ? prices.yearlyPlanNextBillingDate : prices.monthlyPlanNextBillingDate;
    nextBillingDate = External.Tools.dateToString(External.Tools.ddmmyyStringToDate(nextBillingDate));

    if (billingInstance && (oldAmount !== totalWithTax || oldFutureAmount !== nextBillingAmount)) {
      tick().then(() => {
        if (billingInstance) {
          billingInstance.onAmountChange();
        }
      });
    }
  };

  let selectPeriod = (period) => {
    if (selectedPeriod === period || subscribing) {
      return;
    }
    selectedPeriod = period;
    refreshPlanTotalPrices();
  };

  let subscribe = async () => {
    if (subscribing) {
      return;
    }
    subscribing = true;
    let data = {
      selectedPlan: plan.id,
      billingType: selectedPeriod,
      vatPercent: taxPercent,
      discountCode: discountCode
    };

    if (billingInstance) {
      try {
        data = {
          ...data,
          ...await billingInstance.exportUpdatedBillingDetails()
        };
      } catch (e) {
        subscribing = false;
        External.Log.error(e);
        return;
      }
    }

    External.CPanel.disableUI(true);
    let result = await makeWebsiteRequest({
      action: 'confirmPayment',
      data: data
    });

    if (result.ok) {
      await onPaymentSubmitted(result);
    }
    External.CPanel.enableUI();
    subscribing = false;
  };

  let onPaymentSubmitted = async (result) => {
    if (result.data === 'REQUIRES_ACTION') {
      if (billingInstance) {
        await billingInstance.completeRequiredPaymentActions();
      }
      return;
    }

    if (External.isUndefined(result.data.useTax)) {
      dispatch('subscribed', {
        plan: result.data,
        total: totalWithTax,
        currency: currency
      });
    } else {
      External.Tools.showWindow({
        title: '',
        width: 400,
        overflowY: 'hidden',
        markup: External.Templates.get('billingTaxUpdated'),
        showCloseButton: true,
        customClass: 'billing-payment-accepted'
      });

      activeVatPercent = result.data.useTax;
      refreshPlanTotalPrices();
    }
  };

  onMount(() => {
    init();
  });
</script>

<div class="sections-wrapper members-wrapper email-campaign-wrapper" style="margin-top: 35px;">

  <div class="flex flex-col align-center">
    <div class="text-wrapper">
      <h1 class="members-title f-left t-center" style="margin-bottom: 0px; font-size: 24px; color: #444;">
        <b>{M.SELECTED_PLAN}: {plan.name}</b>
      </h1>
    </div>

    <a on:click={cancel} class="middle-button gray discard-billing flex-none mt-15 mb-0">
      <p class="white">&larr;</p>
      {M.GO_BACK}
    </a>
  </div>

  <div class="flex flex-row justify-center f-left col20 pricetables mt-0">
    <div class="section-bg col12" style="box-shadow: none;">
      <div class="payment-content col20 f-left">
        <div class="price-option">
          <div class="price-section">
            <input type="radio" name="planPeriod" value="YEARLY" id="radioPrice1" class="css-checkbox" checked={selectedPeriod === 'YEARLY'}
                   on:change={() => selectPeriod('YEARLY')} />
            <label for="radioPrice1" class="css-label">
              <h3>
                <mark data-role="yearlyPricePerMonth">{External.WebPlatform.currencyWithCodeToStr(yearlyPerMonth, currency, External.platformCountry)}</mark>
                <span data-role="yearlyPricePerMonthInfo">/{M.MONTH?.toLowerCase() || 'month'}
                  ({External.WebPlatform.currencyWithCodeToStr(yearlyPerMonth, currency, External.platformCountry)} x 12 {M.MONTHS})</span>
                {#if saving > 0}
                  <p class="save-badge">{M.SAVE_MONEY} {saving}%</p>
                {/if}
              </h3>
              {#if External.platformId === 1}
                <h6>{M.CANCEL_ANYTIME}, <a class="blue-text">{M.FREE_DOMAIN}</a>, {M.AND_REFUND}</h6>
              {/if}
            </label>
          </div>
          <div class="price-section">
            <input type="radio" name="planPeriod" value="MONTHLY" id="radioPrice2" class="css-checkbox" checked={selectedPeriod === 'MONTHLY'}
                   on:change={() => selectPeriod('MONTHLY')} />
            <label for="radioPrice2" class="css-label">
              <h3>
                <mark data-role="monthlyPrice">{External.WebPlatform.currencyWithCodeToStr(monthlyPrice, currency, External.platformCountry)}</mark>
                <span>/{M.MONTH?.toLowerCase() || 'month'} ({M.MONTH_TO_MONTH})</span>
              </h3>
              {#if External.platformId === 1}
                <h6>{M.CANCEL_ANYTIME_2}</h6>
              {/if}
            </label>
          </div>
        </div>

        {#if !loading}
          <BillingInfo {currentPlan} {vatRates} {paymentProcessor} amount={totalWithTax} futureAmount={nextBillingAmount} {currency} inCheckout={true}
                       on:tax-change={(event) => onTaxChange(event)} bind:this={billingInstance} />
        {/if}

        <div class="sections-wrapper2 mt-0">
          <section class="total-section mt-0">
            <h3 class="total-cost">
              <span data-role="planType">
                {#if currentPlan.status === 'ACTIVE'}
                  {M.SWITCH_TO} {plan.name} - {M['BILLING_TYPE_' + selectedPeriod]}
                {:else}
                  {plan.name} - {M['BILLING_TYPE_' + selectedPeriod]}
                  {#if selectedPeriod === 'YEARLY'}
                    ({External.WebPlatform.currencyWithCodeToStr(planFullPriceNoTax / 12, currency, External.platformCountry)} x 12m)
                  {/if}
                {/if}
              </span>
              <mark data-role="planPrice">{External.WebPlatform.currencyWithCodeToStr(planFullPriceNoTax, currency, External.platformCountry)}</mark>
            </h3>
            <!--
            <AddonsList />
            -->
            {#if creditNoTax > 0.001 || creditNoTax < -0.001}
              <h3 class="total-cost" data-role="creditRow"><span>{M.PLAN_REMAINING_CREDIT}</span>
                <mark data-role="planCredit">{External.WebPlatform.currencyWithCodeToStr(creditNoTax, currency, External.platformCountry)}</mark>
              </h3>
            {/if}
            <div class="promo-code">
              {#if totalNoTax > 0 && !hasDiscount && !discountFieldVisible}
                <a class="promo-code-link" on:click={() => discountFieldVisible = true}>
                  {M.USE_PROMO}
                </a>
              {/if}
              {#if discountFieldVisible && !hasDiscount}
                <div class="promo-code-form" in:fade>
                  <input type="text" bind:value={promoCodeFieldValue} on:keyup={(event) => onPromoCodeKeyUp(event)} />
                  <a class="middle-button gray" on:click={applyPromoCode}>
                    {M.APPLY}
                  </a>
                  <a class="promo-code-remove-link" on:click={() => discountFieldVisible = false}>
                    {M.CANCEL}
                  </a>
                </div>
              {/if}

              {#if hasDiscount}
                <div class="promo-code-discount">
                  <h3 class="total-cost" data-role="promoCodeDiscount">
                    <span data-role="promoCodePercent">{M.PROMO_CODE} {discountCode} (-{discountPercent}%)</span>
                    <mark data-role="promoCodeValue">{External.WebPlatform.currencyWithCodeToStr(-discountValue, currency, External.platformCountry)}</mark>
                  </h3>
                  <a class="promo-code-remove-link" on:click={removePromoCode}>
                    {M.REMOVE_PROMO}
                  </a>
                </div>
              {/if}
            </div>
            {#if prices.pendingFees > 0.01}
              <h3 class="total-cost"><span>Pending Project Fees</span>
                <mark data-role="totalNoVat">{External.WebPlatform.currencyWithCodeToStr(prices.pendingFees, currency, External.platformCountry)}</mark>
              </h3>
            {/if}
            {#if taxPercent > 0}
              <h3 class="total-cost vat-cost" data-role="totalNoVatRow"><span>{M.TOTAL_NO_VAT}</span>
                <mark data-role="totalNoVat">{External.WebPlatform.currencyWithCodeToStr(totalNoTax, currency, External.platformCountry)}</mark>
              </h3>
              <h3 class="total-cost" data-role="vatRow"><span data-role="planVatPercent">{M.VAT} ({taxPercent}%)</span>
                <mark data-role="planVatValue">{External.WebPlatform.currencyWithCodeToStr(tax, currency, External.platformCountry)}</mark>
              </h3>
            {/if}
            <h3 class="total-cost final-cost"><span>{M.TOTAL} </span>
              {#if totalWithTax < 0}
                <span data-role="refundMessage">
                  ({M.WILL_BE_REFUNDED} {External.WebPlatform.currencyWithCodeToStr(-1 * totalWithTax, currency, External.platformCountry)})
                </span>
              {/if}
              <mark data-role="totalPrice">{External.WebPlatform.currencyWithCodeToStr(totalWithTax, currency, External.platformCountry)}</mark>
            </h3>

            <h6 class="t-center" style="margin-top:20px;">
              {M.NEXT_BILLING_ON}
              <span data-role="nextBillingInfo" class="bold" style="font-family: Arial, sans-serif; color: #777;">
                {#if isLifetime}
                  ∞
                {:else}
                  {nextBillingDate} - {External.WebPlatform.currencyWithCodeToStr(nextBillingAmount, currency, External.platformCountry)}
                {/if}
              </span>.
              {M.NEW_PAYMENT_DESCRIPTION}
              <a href="//{External.Website.rootDomain}/privacy-policy" target="_blank" class="blue-text">
                {M.PRIVACY_POLICY}
              </a>
              {M.AND}
              <a href="//{External.Website.rootDomain}/terms-and-conditions" target="_blank" class="blue-text">
                {M.TERMS_OF_SERVICE}
              </a>.
            </h6>

            <div class="subscribe-button-wrapper">
              <a on:click={subscribe} class="middle-button gray subscribe-button" class:disabled={subscribing}>
                {M.SUBSCRIBE}
              </a>
            </div>
            <div class="col20 f-left center-vertically mt-45 money-back-badge secure-payment-badge"><img
              src="{External.resources}/images/lock.svg"><span><b>{M.SECURE_PAYMENT}</b><br /><span>{M.SECURE_PAYMENT_DESC}</span></span>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</div>
